import { useState } from 'react';
import { Button } from '../ui/button';
import {
    Phone, Mail, Building2, Edit2, Trash2,
    Search, PlusCircle, Star
} from 'lucide-react';
import { cn } from '../ui/utils';
import { Vendor } from '../../types/vendor';
import { motion } from 'framer-motion';

interface VendorListProps {
    vendors: Vendor[];
    onEdit: (vendor: Vendor) => void;
    onDelete: (vendorId: string) => void;
    onInvite: () => void;
    onAdd: () => void;
    isDarkMode: boolean;
}

export function VendorList({ vendors, onEdit, onDelete, onInvite, onAdd, isDarkMode }: VendorListProps) {
    const [searchTerm, setSearchTerm] = useState('');

    const filteredVendors = vendors.filter(v =>
        v.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        v.services.some(s => s.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="space-y-4">
            {/* Header / Actions */}
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-center mb-6">
                <div className="relative w-full sm:w-auto sm:flex-1 max-w-md">
                    <Search className={cn("absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5", isDarkMode ? "text-gray-500" : "text-gray-400")} />
                    <input
                        placeholder="Search vendors..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={cn(
                            "w-full pl-12 pr-4 py-4 rounded-2xl text-sm border focus:outline-none focus:ring-2 focus:ring-purple-500",
                            isDarkMode
                                ? "bg-slate-800/50 border-slate-700 text-white placeholder-gray-500"
                                : "bg-gray-100 border-gray-200 text-gray-900 placeholder-gray-500"
                        )}
                    />
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                    <Button variant="outline" onClick={onInvite} className="flex-1 sm:flex-none">
                        <Mail className="w-4 h-4 mr-2" />
                        Invite
                    </Button>
                    <Button onClick={onAdd} className="bg-blue-600 hover:bg-blue-700 text-white flex-1 sm:flex-none">
                        <PlusCircle className="w-4 h-4 mr-2" />
                        Add Vendor
                    </Button>
                </div>
            </div>

            {/* List */}
            <div className="space-y-3 mb-20">
                {filteredVendors.map((vendor, index) => (
                    <motion.div
                        key={vendor.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className={cn(
                            "p-0.5 rounded-2xl shadow-sm",
                            `bg-gradient-to-br ${vendor.status === 'active' ? 'from-purple-500 to-indigo-600' : 'from-gray-400 to-gray-500'}`
                        )}
                    >
                        <div className={cn(
                            "rounded-[0.9rem] p-4",
                            isDarkMode ? "bg-slate-900" : "bg-white"
                        )}>
                            {/* Header */}
                            <div className="flex items-start justify-between mb-4">
                                <div className="flex items-start gap-3 flex-1">
                                    <div className={cn(
                                        "w-12 h-12 rounded-xl flex items-center justify-center shrink-0",
                                        `bg-gradient-to-br ${vendor.status === 'active' ? 'from-purple-500 to-indigo-600' : 'from-gray-400 to-gray-500'}`
                                    )}>
                                        <Building2 className="w-6 h-6 text-white" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className={cn("text-base font-bold mb-0.5 truncate", isDarkMode ? "text-white" : "text-gray-900")}>
                                            {vendor.displayName || vendor.name}
                                        </div>
                                        <div className={cn("text-xs mb-2 truncate", isDarkMode ? "text-gray-400" : "text-gray-500")}>
                                            {vendor.contactName} {vendor.contactTitle ? `• ${vendor.contactTitle}` : ''}
                                        </div>
                                        {/* Rating */}
                                        <div className="flex items-center gap-1">
                                            {[...Array(5)].map((_, i) => (
                                                <Star
                                                    key={i}
                                                    className={cn(
                                                        "w-3 h-3",
                                                        i < Math.floor(vendor.rating?.overall || 0)
                                                            ? "fill-yellow-500 text-yellow-500"
                                                            : "text-gray-300 dark:text-gray-600"
                                                    )}
                                                />
                                            ))}
                                            <span className={cn("text-xs ml-1", isDarkMode ? "text-gray-400" : "text-gray-600")}>
                                                {vendor.rating?.overall || 0}
                                            </span>
                                            {vendor.rating?.totalReviews !== undefined && (
                                                <span className={cn("text-[10px] ml-2", isDarkMode ? "text-gray-500" : "text-gray-500")}>
                                                    ({vendor.rating.totalReviews} reviews)
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => onEdit(vendor)}
                                        aria-label="Edit vendor"
                                        className={cn(
                                            "w-8 h-8 rounded-lg flex items-center justify-center transition-transform active:scale-90",
                                            isDarkMode ? "bg-slate-800 text-blue-400" : "bg-blue-50 text-blue-600"
                                        )}
                                    >
                                        <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => onDelete(vendor.id)}
                                        aria-label="Delete vendor"
                                        className={cn(
                                            "w-8 h-8 rounded-lg flex items-center justify-center transition-transform active:scale-90",
                                            isDarkMode ? "bg-slate-800 text-red-400" : "bg-red-50 text-red-600"
                                        )}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            {/* Contact Info */}
                            <div className="grid grid-cols-2 gap-2 mb-4">
                                <a href={`tel:${vendor.phone}`} className={cn(
                                    "p-2.5 rounded-xl flex items-center gap-2 transition-colors",
                                    isDarkMode ? "bg-slate-800/50 hover:bg-slate-800" : "bg-gray-50 hover:bg-gray-100"
                                )}>
                                    <Phone className={cn("w-3.5 h-3.5", isDarkMode ? "text-purple-400" : "text-purple-600")} />
                                    <span className={cn("text-xs font-medium", isDarkMode ? "text-gray-300" : "text-gray-700")}>
                                        Call
                                    </span>
                                </a>
                                <a href={`mailto:${vendor.email}`} className={cn(
                                    "p-2.5 rounded-xl flex items-center gap-2 transition-colors",
                                    isDarkMode ? "bg-slate-800/50 hover:bg-slate-800" : "bg-gray-50 hover:bg-gray-100"
                                )}>
                                    <Mail className={cn("w-3.5 h-3.5", isDarkMode ? "text-purple-400" : "text-purple-600")} />
                                    <span className={cn("text-xs font-medium truncate", isDarkMode ? "text-gray-300" : "text-gray-700")}>
                                        Email
                                    </span>
                                </a>
                            </div>
                        </div>
                    </motion.div>
                ))}
            </div>

            {filteredVendors.length === 0 && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                    <div className={cn("p-4 rounded-full mb-4", isDarkMode ? "bg-slate-800" : "bg-gray-100")}>
                        <Building2 className={cn("w-8 h-8", isDarkMode ? "text-slate-500" : "text-gray-400")} />
                    </div>
                    <h3 className={cn("text-lg font-medium", isDarkMode ? "text-white" : "text-gray-900")}>
                        No vendors found
                    </h3>
                    <p className={cn("text-sm", isDarkMode ? "text-slate-400" : "text-gray-500")}>
                        Add your first vendor or try a different search
                    </p>
                </div>
            )}
        </div>
    );
}
